if (!customElements.get('lcn-predictive-search')) {
  customElements.define('lcn-predictive-search', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.form = this.closest('form');
      this.input = this.querySelector('input[name="q"]');
      this.dropdown = this.querySelector('[data-lcn-dropdown]');
      this.results = this.querySelector('[data-lcn-results]');
      this.status = this.querySelector('[data-lcn-status]');
      if (!this.form || !this.input || !this.dropdown || !this.results) return;
      this.events = new AbortController();
      this.media = matchMedia('(min-width: 900px)');
      this.cache = new Map();
      this.active = -1;
      const signal = this.events.signal;
      this.input.addEventListener('input', () => this.onInput(), {signal});
      this.input.addEventListener('focus', () => this.schedule(), {signal});
      this.input.addEventListener('keydown', event => this.onKey(event), {signal});
      this.form.addEventListener('submit', () => this.close(), {signal});
      document.addEventListener('focusin', event => {
        if (!this.form.contains(event.target)) this.close();
      }, {signal});
      window.addEventListener('resize', () => this.fit(), {signal});
      window.visualViewport?.addEventListener('resize', () => this.fit(), {signal});
      document.addEventListener('pointerdown', event => {
        if (!this.form.contains(event.target)) this.close();
      }, {signal});
      this.closest('.lcn-header')?.querySelector('lcn-navigation')?.addEventListener('pointerenter', () => this.close(), {signal});
      this.media.addEventListener('change', () => this.close(), {signal});
    }

    disconnectedCallback() {
      this.close();
      this.events?.abort();
      this.events = null;
    }

    onInput() {
      this.close();
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();
      if (!this.media.matches || term.length < 2) return;
      this.timer = setTimeout(() => this.fetchResults(term), 200);
    }

    async fetchResults(term) {
      if (!this.media.matches || this.input.value.trim() !== term || !this.form.matches(':focus-within')) return;
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }
      this.request?.abort();
      this.request = new AbortController();
      const signal = this.request.signal;
      this.results.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.dataset.suggestUrl, location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', 'lcn-search-suggestions');
        url.searchParams.set('resources[type]', 'product,query');
        url.searchParams.set('resources[limit]', '3');
        url.searchParams.set('resources[limit_scope]', 'each');
        const response = await fetch(url, {signal});
        if (!response.ok) throw new Error(String(response.status));
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = document.querySelector('[data-lcn-suggestions]');
        if (!content) throw new Error('Missing suggestions');
        if (signal.aborted || this.input.value.trim() !== term || !this.media.matches || !this.form.matches(':focus-within')) return;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(term, content.innerHTML);
        this.render(content.innerHTML);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        if (this.request?.signal === signal) this.results.removeAttribute('aria-busy');
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.options = [...this.results.querySelectorAll('[role="option"]')];
      this.options.forEach((option, index) => {
        option.id = this.results.id + '-option-' + index;
      });
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', 'true');
      this.dropdown.hidden = false;
      this.fit();
      this.status.textContent = this.results.querySelector('[data-lcn-result-status]')?.textContent || '';
    }

    fit() {
      if (this.dropdown.hidden) return;
      const bottom = window.visualViewport ? window.visualViewport.offsetTop + window.visualViewport.height : innerHeight;
      this.dropdown.style.setProperty('--lcn-search-available-height', Math.max(96, bottom - this.dropdown.getBoundingClientRect().top - 12) + 'px');
    }

    onKey(event) {
      if (event.key === 'Escape') {
        this.close();
        event.preventDefault();
        return;
      }
      if (this.dropdown.hidden || !this.options?.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.active = this.active < 0 ? (direction === 1 ? 0 : this.options.length - 1) : (this.active + direction + this.options.length) % this.options.length;
        this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
        const option = this.options[this.active];
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({block: 'nearest'});
      } else if (event.key === 'Enter' && this.active >= 0) {
        event.preventDefault();
        this.options[this.active].click();
      }
    }

    close() {
      clearTimeout(this.timer);
      this.request?.abort();
      if (!this.input) return;
      this.dropdown.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.results.removeAttribute('aria-busy');
      this.active = -1;
      if (this.status) this.status.textContent = '';
    }
  });
}
